import React, { useState } from "react";
import { IoClose } from "react-icons/io5";
import {
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiExternalLink,
  FiPlusCircle,
  FiTerminal,
  FiFileText,
} from "react-icons/fi";
import { createPrediction } from "api/predictions.api";
import { createPromise } from "api/promises.api";
import { toast } from "react-toastify";

const ExecutionLogsModal = ({ isOpen, onClose, run }) => {
  const [activeTab, setActiveTab] = useState("findings"); // "findings" | "logs"
  const [creatingItemIdx, setCreatingItemIdx] = useState(null);

  if (!isOpen || !run) return null;

  const findings = run.findings || [];
  const logs = run.logs || [];

  const handleCreateFromFinding = async (finding, index) => {
    setCreatingItemIdx(index);
    try {
      if (finding.kind === "prediction") {
        const docRes = await createPrediction({
          title: finding.title || "",
          description: finding.publishSummary || "",
          content: finding.publishArticle || "",
          categories: finding.categories || ["Քաղաքականություն"],
          level: finding.level || 1,
          deadline: finding.deadline || null,
          predictor: finding.predictor || {
            firstName: finding.authorName?.split(" ")[0] || "",
            lastName: finding.authorName?.split(" ").slice(1).join(" ") || "",
            degree: finding.authorRole || "",
          },
          image: finding.image || null,
          createdAt: Date.now(),
          type: "pending",
          published: false,
        });
        if (docRes?.id) {
          toast.success("Կանխատեսումը հաջողությամբ ստեղծված է:");
          window.open(`/predictions/manage/${docRes.id}`, "_blank");
        }
      } else if (finding.kind === "promise") {
        const docRes = await createPromise({
          title: finding.title || "",
          description: finding.publishSummary || "",
          content: finding.publishArticle || "",
          categories: finding.categories || ["Քաղաքականություն"],
          level: finding.level || 1,
          deadline: finding.deadline || null,
          predictor: finding.predictor || {
            firstName: finding.authorName?.split(" ")[0] || "",
            lastName: finding.authorName?.split(" ").slice(1).join(" ") || "",
            degree: finding.authorRole || "",
          },
          image: finding.image || null,
          createdAt: Date.now(),
          type: "pending",
          published: false,
        });
        if (docRes?.id) {
          toast.success("Խոստումը հաջողությամբ ստեղծված է:");
          window.open(`/promises/manage/${docRes.id}`, "_blank");
        }
      }
    } catch (err) {
      console.error("Creation error:", err);
      toast.error("Ստեղծման սխալ՝ " + err.message);
    } finally {
      setCreatingItemIdx(null);
    }
  };

  const getStatusBadge = () => {
    switch (run.status) {
      case "success":
        return (
          <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">
            <FiCheckCircle size={14} /> Հաջողված
          </span>
        );
      case "failed":
        return (
          <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-bold">
            <FiAlertCircle size={14} /> Ձախողված
          </span>
        );
      case "running":
        return (
          <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-bold animate-pulse">
            <FiClock size={14} /> Ընթացքի մեջ
          </span>
        );
      default:
        return (
          <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
            {run.status}
          </span>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-4xl text-slate-800 shadow-2xl max-h-[90vh] flex flex-col my-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-slate-900">
                {run.automationName || "Ավտոմատացման գործարկում"}
              </h2>
              {getStatusBadge()}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Գործարկվել է՝{" "}
              {run.startedAt?.toDate
                ? run.startedAt.toDate().toLocaleString("hy-AM")
                : "Նոր"}
              {run.findingsCount !== undefined && (
                <span className="ml-2 font-semibold text-indigo-600">
                  • Գտնված նյութեր՝ {run.findingsCount}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition"
          >
            <IoClose size={22} />
          </button>
        </div>

        {/* Modal Tabs */}
        <div className="flex border-b border-slate-200 px-6 gap-4 bg-slate-50">
          <button
            onClick={() => setActiveTab("findings")}
            className={`flex items-center gap-2 py-3 text-sm font-semibold border-b-2 transition ${
              activeTab === "findings"
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <FiFileText size={16} /> Գտնված արդյունքներ ({findings.length})
          </button>
          <button
            onClick={() => setActiveTab("logs")}
            className={`flex items-center gap-2 py-3 text-sm font-semibold border-b-2 transition ${
              activeTab === "logs"
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <FiTerminal size={16} /> Կատարման լոգեր ({logs.length})
          </button>
        </div>

        {/* Modal Body */}
        <div className="overflow-y-auto p-6 flex-1 max-h-[65vh]">
          {activeTab === "findings" ? (
            findings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {findings.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col justify-between hover:border-slate-300 hover:shadow-md transition"
                  >
                    <div>
                      {/* Kind Badge & Predictor status */}
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            item.kind === "prediction"
                              ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
                              : item.kind === "promise"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-purple-50 text-purple-700 border border-purple-200"
                          }`}
                        >
                          {item.kind === "prediction"
                            ? "Կանխատեսում"
                            : item.kind === "promise"
                            ? "Խոստում"
                            : "Հարցում"}
                        </span>

                        {item.isExistingPredictor ? (
                          <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 font-semibold">
                            ✓ Կապված կանխատեսող
                          </span>
                        ) : (
                          <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 font-semibold">
                            Նոր հեղինակ
                          </span>
                        )}
                      </div>

                      <h3 className="text-sm font-bold text-slate-900 mb-2 line-clamp-2">
                        {item.title}
                      </h3>

                      {item.authorName && (
                        <p className="text-xs text-slate-700 font-medium mb-1">
                          Հեղինակ՝{" "}
                          <span className="text-indigo-600 font-semibold">
                            {item.authorName}
                          </span>{" "}
                          {item.authorRole && (
                            <span className="text-slate-500">
                              ({item.authorRole})
                            </span>
                          )}
                        </p>
                      )}

                      {item.deadline && (
                        <p className="text-xs text-amber-700 font-medium mb-1">
                          Ժամկետ՝ {item.deadline}
                        </p>
                      )}

                      {item.publishSummary && (
                        <p className="text-xs text-slate-500 line-clamp-3 mb-3 italic">
                          "{item.publishSummary}"
                        </p>
                      )}
                    </div>

                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2 mt-2">
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600 transition"
                        >
                          <FiExternalLink size={12} /> {item.site || "Աղբյուր"}
                        </a>
                      )}

                      <button
                        onClick={() => handleCreateFromFinding(item, idx)}
                        disabled={creatingItemIdx === idx}
                        className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-bold text-white transition shadow-sm ${
                          item.kind === "promise"
                            ? "bg-emerald-600 hover:bg-emerald-700"
                            : "bg-indigo-600 hover:bg-indigo-700"
                        } disabled:opacity-50`}
                      >
                        <FiPlusCircle size={14} />
                        {creatingItemIdx === idx
                          ? "Ստեղծվում է..."
                          : item.kind === "promise"
                          ? "Ստեղծել խոստում"
                          : "Ստեղծել կանխատեսում"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <FiFileText size={36} className="mx-auto mb-2 opacity-50" />
                <p>Այս գործարկման ընթացքում նյութեր չեն գրանցվել:</p>
              </div>
            )
          ) : (
            <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 font-mono text-xs space-y-2 text-slate-200">
              {logs.length > 0 ? (
                logs.map((log, index) => (
                  <div
                    key={index}
                    className="border-b border-slate-800/80 pb-1 flex items-start gap-2"
                  >
                    <span className="text-indigo-400 select-none">[{index + 1}]</span>
                    <span className="flex-1">{log}</span>
                  </div>
                ))
              ) : (
                <p className="text-slate-500">Լոգերը բացակայում են:</p>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition"
          >
            Փակել
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecutionLogsModal;
